import React from "react";
import {
  useLocation,
  Route,
  Switch,
  Redirect,
  useHistory,
} from "react-router-dom";
import AdminNavbar from "components/Navbars/AdminNavbar.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import routes from "routes.js";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "store/actions/authAction";
import {
  socket,
  addUserSocket,
  pageRefresh,
  disconnectSocket,
  onAdminStatusEvent,
} from "config/socketConfig";
import { toast } from "react-toastify";
import { RepositoryFactory } from "repository/RepositoryFactory";

const roleBasedRoutes = {
  admin: "all",
  resolutionTeam: ["/admin/index", "/admin/orders", "/admin/resolution"],
  supportPerson: [
    "/admin/index",
    "/admin/support-team",
    "/admin/tickets",
    "/admin/ticket-details/:id",
     "/admin/posted-work-orders",
    "/admin/orders",
    "/admin/special-orders",
    "/admin/message",
    "/admin/disputes",
    "/admin/disputes-details/:id",
    "/admin/policies",
    "/admin/reports",
    "/admin/report-details/:id",
    "/admin/checkr-reports",
    "/admin/payments",
    "/admin/name-requests",
    "/admin/job-deletion",
    "/admin/supportchat",
  ],
  sales: [
    "/admin/users",
    "/admin/posted-work-orders",
    "/admin/orders",
    "/admin/special-orders",
    "/admin/waitlist",
  ],
  seo_admin: ["/admin/blogs", "/admin/addBlog", "/admin/editBlog/:id"],
  supportAdmin: [
    "/admin/posted-work-orders",
    "/admin/orders",
    "/admin/special-orders",
    "/admin/message",
    "/admin/disputes",
    "/admin/disputes-details/:id",
    "/admin/policies",
    "/admin/reports",
    "/admin/report-details/:id",
    "/admin/checkr-reports",
    "/admin/payments",
    "/admin/name-requests",
    "/admin/job-deletion",
    "/admin/supportchat",
    "/admin/chatbot-knowledge",
    "/admin/add-chatbot-article",
    "/admin/edit-chatbot-article/:id",
    "/admin/chatbot-settings",
  ],
};

const Admin = (props) => {
  const mainContent = React.useRef(null);
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { user, uid, email: authEmail, authToken } = useSelector((state) => state.auth);

  const parseJwt = (token) => {
    try {
      if (!token) return null;
      const base64Url = token.split(".")[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (e) {
      return null;
    }
  };

  const jwtData = React.useMemo(() => parseJwt(authToken), [authToken]);
  const userRole = user?.role || jwtData?.role;
  const currentUserId = (
    user?.id ||
    user?._id ||
    user?.userId ||
    user?.adminId ||
    uid ||
    jwtData?.id ||
    jwtData?._id ||
    jwtData?.userId ||
    jwtData?.sub ||
    ""
  ).toString();
  const currentUserEmail = (
    user?.email ||
    authEmail ||
    jwtData?.email ||
    ""
  ).trim().toLowerCase();
  const currentUserName = (
    user?.name ||
    `${user?.firstName || ""} ${user?.lastName || ""}`.trim() ||
    user?.firstName ||
    jwtData?.name ||
    ""
  ).trim();

  const checkIsDisabled = (admin) => {
    if (!admin) return false;
    const s = String(admin.status ?? "").toLowerCase().trim();
    const act = String(admin.isActive ?? admin.active ?? "").toLowerCase().trim();
    return (
      admin.status === false ||
      s === "false" ||
      s === "0" ||
      s === "inactive" ||
      s === "disabled" ||
      s === "deactivated" ||
      admin.isActive === false ||
      act === "false" ||
      act === "0" ||
      act === "inactive" ||
      act === "disabled" ||
      admin.active === false ||
      admin.isBlocked === true ||
      admin.isDeleted === true ||
      admin.deleted === true
    );
  };

  const verifyAccountStatusRef = React.useRef(null);
  const isLoggingOutRef = React.useRef(false);

  React.useEffect(() => {
    if (!currentUserId && !currentUserEmail) return;

    if (currentUserId) {
      if (!socket.connected) {
        addUserSocket(currentUserId, userRole);
      } else {
        pageRefresh(currentUserId, userRole);
      }
    }

    const handleForceLogout = (data) => {
      console.log("Real-time admin update/logout event received:", data);
      const targetId = (data?.userId || data?.id || data?.adminId || data?._id || "").toString();
      const targetEmail = (data?.email || "").trim().toLowerCase();

      const isTargetUser = Boolean(
        (targetId && currentUserId && targetId === currentUserId) ||
        (targetEmail && currentUserEmail && targetEmail === currentUserEmail)
      );
      const isDirectSocketEvent = Boolean(
        !targetId &&
          !targetEmail &&
          (data?.event === "forceLogout" ||
            data?.event === "accountDisabled" ||
            data?.event === "accountDeleted")
      );

      if (isTargetUser || isDirectSocketEvent) {
        const isDeleteAction =
          data?.action === "delete" ||
          data?.deleted === true ||
          data?.event === "userDeleted" ||
          data?.event === "adminDeleted" ||
          data?.event === "accountDeleted";
        const isDisabledAction =
          data?.status === false ||
          data?.status === "false" ||
          data?.action === "disable" ||
          data?.disabled === true ||
          data?.event === "accountDisabled";

        if (isDeleteAction || isDisabledAction) {
          if (isLoggingOutRef.current) return;
          isLoggingOutRef.current = true;

          const toastMessage = isDeleteAction
            ? "Your account has been deleted by an administrator."
            : "Your account has been disabled by an administrator.";

          if (!toast.isActive("account-disabled-toast")) {
            toast.error(toastMessage, {
              toastId: "account-disabled-toast",
            });
          }
          disconnectSocket();
          localStorage.clear();
          localStorage.removeItem("auth");
          dispatch(logout());
          setTimeout(() => {
            window.location.href = "/auth/login";
          }, 1500);
        }
      }
    };

    const unsubscribeCrossTab = onAdminStatusEvent(handleForceLogout);

    const verifyAccountStatus = async () => {
      try {
        const adminsRepo = RepositoryFactory.get("admins");
        let me = null;

        // 1. Direct fetch by ID if currentUserId is available
        if (currentUserId) {
          try {
            const res = await adminsRepo.getAdminById(currentUserId);
            const adminObj = res?.data?.data || res?.data;
            if (adminObj && (adminObj.id || adminObj._id || adminObj.email)) {
              me = adminObj;
            }
          } catch (errId) {}
        }

        // 2. Multi-page getAdmins fetch
        if (!me) {
          try {
            const { data } = await adminsRepo.getAdmins(1, 50);
            const adminList = data?.data || [];
            me = adminList.find(
              (a) =>
                (currentUserId && (a?.id || a?._id)?.toString() === currentUserId) ||
                (currentUserEmail && (a?.email || "").trim().toLowerCase() === currentUserEmail)
            );

            const totalPages = data?.totalPages || 1;
            if (!me && totalPages > 1) {
              const maxPagesToCheck = Math.min(totalPages, 10);
              for (let p = 2; p <= maxPagesToCheck; p++) {
                try {
                  const { data: pageData } = await adminsRepo.getAdmins(p, 50);
                  const pList = pageData?.data || [];
                  me = pList.find(
                    (a) =>
                      (currentUserId && (a?.id || a?._id)?.toString() === currentUserId) ||
                      (currentUserEmail && (a?.email || "").trim().toLowerCase() === currentUserEmail)
                  );
                  if (me) break;
                } catch (ePage) {}
              }
            }
          } catch (e) {}
        }

        // 3. Fallback search by name/email
        if (!me) {
          const searchTerms = [
            currentUserName,
            user?.firstName,
            user?.lastName,
            currentUserEmail,
          ].filter(Boolean);

          for (const term of searchTerms) {
            try {
              const { data: sData } = await adminsRepo.adminSearchByName(term);
              const searchList = sData?.data || [];
              me = searchList.find(
                (a) =>
                  (currentUserId && (a?.id || a?._id)?.toString() === currentUserId) ||
                  (currentUserEmail && (a?.email || "").trim().toLowerCase() === currentUserEmail)
              );
              if (me) break;
            } catch (errSearch) {}
          }
        }

        if (me && checkIsDisabled(me)) {
          handleForceLogout({
            userId: currentUserId,
            id: currentUserId,
            email: currentUserEmail,
            status: false,
            action: "disable",
          });
        }
      } catch (err) {
        // Handled by axios interceptor if 401/403
      }
    };

    verifyAccountStatusRef.current = verifyAccountStatus;
    verifyAccountStatus();

    const onVisibilityOrFocus = () => {
      if (document.visibilityState === "visible") {
        verifyAccountStatus();
      }
    };
    window.addEventListener("focus", onVisibilityOrFocus);
    document.addEventListener("visibilitychange", onVisibilityOrFocus);
    const intervalId = setInterval(verifyAccountStatus, 5000);

    socket.on("adminStatusChanged", handleForceLogout);
    socket.on("userStatusChanged", handleForceLogout);
    socket.on("adminDeleted", handleForceLogout);
    socket.on("userDeleted", handleForceLogout);
    socket.on("accountDisabled", handleForceLogout);
    socket.on("accountDeleted", handleForceLogout);
    socket.on("forceLogout", handleForceLogout);

    return () => {
      unsubscribeCrossTab();
      window.removeEventListener("focus", onVisibilityOrFocus);
      document.removeEventListener("visibilitychange", onVisibilityOrFocus);
      clearInterval(intervalId);
      socket.off("adminStatusChanged", handleForceLogout);
      socket.off("userStatusChanged", handleForceLogout);
      socket.off("adminDeleted", handleForceLogout);
      socket.off("userDeleted", handleForceLogout);
      socket.off("accountDisabled", handleForceLogout);
      socket.off("accountDeleted", handleForceLogout);
      socket.off("forceLogout", handleForceLogout);
    };
  }, [currentUserId, currentUserEmail, userRole, dispatch]);

  React.useEffect(() => {
    if (verifyAccountStatusRef.current) {
      verifyAccountStatusRef.current();
    }
  }, [location.pathname]);

  React.useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainContent.current.scrollTop = 0;
  }, [location]);

  // const isAllowedRoute = (path) => {
  //   if (roleBasedRoutes[userRole] === "all") return true;
  //   return roleBasedRoutes[userRole]?.includes(path);
  // };

  const isAllowedRoute = (path) => {
    if (roleBasedRoutes[userRole] === "all") return true;

    return roleBasedRoutes[userRole]?.some((route) => {
      if (route.includes(":")) {
        const regex = new RegExp(`^${route.replace(/:[^/]+/g, "([^/]+)")}$`);
        return regex.test(path);
      }
      return route === path;
    });
  };

  React.useEffect(() => {
    if (!userRole) return;
    if (!isAllowedRoute(location.pathname)) {
      const defaultRoute = roleBasedRoutes[userRole]?.[0] || "/admin/index";
      console.log(`Redirecting from ${location.pathname} to ${defaultRoute}`);
      history.replace(defaultRoute);
    }
  }, [location.pathname, userRole, history]);

  const getRoutes = (routes) => {
    return routes.map((prop, key) => {
      if (prop.layout === "/admin") {
        if (isAllowedRoute(prop.layout + prop.path)) {
          return (
            <Route
              exact
              path={prop.layout + prop.path}
              component={prop.component}
              key={key}
            />
          );
        }
      }
      return null;
    });
  };

  // const getRoutes = (routes) => {
  //   return routes.map((prop, key) => {
  //     if (prop.layout === "/admin") {
  //       return (
  //         <Route
  //           path={prop.layout + prop.path}
  //           component={prop.component}
  //           key={key}
  //         />
  //       );
  //     } else {
  //       return null;
  //     }
  //   });
  // };

  const getBrandText = (path) => {
    for (let i = 0; i < routes.length; i++) {
      const routePath = routes[i].layout + routes[i].path;
      
      // Convert route pattern with :params to a regex pattern
      const routeRegex = new RegExp(
        "^" + routePath.replace(/:[^/]+/g, "([^/]+)") + "$"
      );
      
      // Check if current path matches the route pattern
      if (routeRegex.test(props.location.pathname)) {
        return routes[i].name;
      }
    }
    return "Brand";
  };

  return (
    <>
      <Sidebar
        {...props}
        routes={routes}
        logo={{
          innerLink: "/admin/index",
          imgSrc: require("../assets/img/brand/argon-react.png").default,
          imgAlt: "...",
        }}
      />
      <div className="main-content" ref={mainContent}>
        <AdminNavbar
          {...props}
          brandText={getBrandText(props.location.pathname)}
        />
        <Switch>
          {getRoutes(routes)}
          <Redirect from="*" to="/admin/index" />
        </Switch>
      </div>
    </>
  );
};

export default Admin;
